import { InlineNotification, SkeletonText } from '@carbon/react';
import { useTranslation } from '@pos/i18n';
import type { ReactNode } from 'react';
import { BackLink } from '../components/BackLink';
import { asProblem, problemMessage } from '../problems';

/** Page frame for one transaction: back link, loading and error states. */
export function TransactionFrame({
  error,
  loading,
  backTo,
  children,
}: {
  error: unknown;
  loading: boolean;
  /** The list this transaction belongs to. */
  backTo: string;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <section className="pos-admin-page">
      <BackLink to={backTo}>{t('transactions.detail.back')}</BackLink>
      {error ? (
        <InlineNotification kind="error" role="alert" lowContrast hideCloseButton title={problemMessage(t, asProblem(error))} />
      ) : loading ? (
        <SkeletonText heading paragraph lineCount={8} />
      ) : (
        children
      )}
    </section>
  );
}

/** Label and value pairs; empty values are left out. */
export function Facts({ facts }: { facts: [string, ReactNode][] }) {
  return (
    <dl className="pos-orders__facts pos-transactions__facts">
      {facts
        .filter(([, value]) => value !== null && value !== undefined && value !== '')
        .map(([label, value]) => (
          <div key={label} className="pos-transactions__fact">
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
    </dl>
  );
}

export interface TimelineEntry {
  id: string;
  at: string;
  title: string;
  detail?: string;
  by?: string | null;
}

/** What happened, oldest first, with who did it. */
export function Timeline({ entries, format }: { entries: TimelineEntry[]; format(at: string): string }) {
  const { t } = useTranslation();
  if (entries.length === 0) return <p className="pos-orders__muted">{t('transactions.detail.noEvents')}</p>;
  return (
    <ol className="pos-orders__timeline">
      {entries.map((e) => (
        <li key={e.id}>
          <span className="pos-orders__timeline-time">{format(e.at)}</span>
          <span>
            <strong>{e.title}</strong>
            {e.detail && <span className="pos-orders__muted"> · {e.detail}</span>}
          </span>
          <span className="pos-orders__muted">{t('transactions.detail.by', { name: e.by ?? t('transactions.detail.system') })}</span>
        </li>
      ))}
    </ol>
  );
}
